/**
 * Users tab displaying a unified table of all account members
 * Combines pending invites and active users in one table (GCP IAM pattern)
 */
import { Check, ChevronDown, Copy, Trash2, UserPlus, Users, X } from "lucide-react";
import { useState } from "react";

import type { UnifiedMember } from "../types";

import { EditInviteDialog } from "./EditInviteDialog";
import { EditUserDialog } from "./EditUserDialog";
import { InviteUserDialog } from "./InviteUserDialog";
import { RemoveUserConfirmDialog } from "./RemoveUserConfirmDialog";
import { UserRoleBadges } from "./UserRoleBadges";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";



/**
 * Users tab with unified table of invites + active users
 */
export function UsersTab() {
	const [filterRoleId, setFilterRoleId] = useState<string | undefined>(undefined);
	const [filterOpen, setFilterOpen] = useState(false);
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

	// Editing states - clicking row opens the appropriate dialog
	const [editingInvite, setEditingInvite] = useState<{
		id: string;
		email: string;
		accountRoleId: string;
		teamIds: string[];
		teamRoleId: string | null;
	} | null>(null);
	const [editingUser, setEditingUser] = useState<{
		id: string;
		name: string | null;
		email: string;
	} | null>(null);

	// Action states
	const [removingUser, setRemovingUser] = useState<{
		id: string;
		name: string | null;
		email: string;
	} | null>(null);

	// Fetch users
	const usersQuery = trpc.permissionGroups.listAccountUsers.useQuery({
		limit: 50,
		offset: 0,
	});

	// Fetch pending invites
	const invitesQuery = trpc.permissionGroups.listInvites.useQuery();

	// Revoke invite mutation
	const revokeInviteMutation = trpc.permissionGroups.revokeInvite.useMutation({
		onSuccess: () => {
			void invitesQuery.refetch();
		},
	});

	// Fetch roles for filter dropdown
	const rolesQuery = trpc.roles.list.useQuery();

	const selectedFilterRole = rolesQuery.data?.find((r) => r.id === filterRoleId);

	// Combine invites and users into a single list
	const unifiedMembers: UnifiedMember[] = [
		// Pending invites first
		...(invitesQuery.data ?? []).map((invite): UnifiedMember => ({
			type: "invite",
			id: invite.id,
			email: invite.email,
			name: null,
			status: "Invited",
			accountRole: invite.accountRole,
			teamRole: invite.teamRole,
			teamIds: invite.teamIds,
			teams: [] as never[],
			role: undefined as never,
			createdAt: invite.createdAt,
			expiresAt: invite.expiresAt,
			token: invite.token,
		})),
		// Active users
		...(usersQuery.data?.users ?? []).map((user): UnifiedMember => ({
			type: "user",
			id: user.id,
			email: user.email,
			name: user.name,
			status: "Active",
			accountRole: user.accountRole,
			teamRole: undefined as never,
			teamIds: [] as never[],
			teams: user.teams,
			role: user.role,
			createdAt: undefined as never,
			expiresAt: undefined as never,
			token: undefined as never,
		})),
	];

	// Filter by role if selected
	const filteredMembers = filterRoleId
		? unifiedMembers.filter((member) => {
			if (member.type === "user") {
				// Check account role or any team role
				return member.accountRole.id === filterRoleId ||
					member.teams.some((t) => t.role.id === filterRoleId);
			}
			// Invites - check account role or team role
			return member.accountRole?.id === filterRoleId ||
				member.teamRole?.id === filterRoleId;
		})
		: unifiedMembers;

	const isLoading = usersQuery.isLoading || invitesQuery.isLoading;

	/**
	 * Opens the appropriate edit dialog based on member type
	 * @param member - The unified member (user or invite) to edit
	 */
	const handleRowClick = (member: UnifiedMember) => {
		if (member.type === "invite") {
			setEditingInvite({
				id: member.id,
				email: member.email,
				accountRoleId: member.accountRole?.id ?? "",
				teamIds: member.teamIds,
				teamRoleId: member.teamRole?.id ?? null,
			});
		}
		else {
			setEditingUser({
				id: member.id,
				name: member.name,
				email: member.email,
			});
		}
	};

	return (
		<div className="space-y-4">
			{/* Filter Bar with Invite Button */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Popover open={filterOpen} onOpenChange={setFilterOpen}>
						<PopoverTrigger asChild>
							<Button variant="outline" className="justify-between min-w-[200px]">
								{selectedFilterRole ? selectedFilterRole.name : "All Roles"}
								<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-[200px] p-0" align="start">
							<Command>
								<CommandInput placeholder="Search roles..." />
								<CommandList>
									<CommandEmpty>No roles found.</CommandEmpty>
									<CommandGroup>
										<CommandItem
											onSelect={() => {
												setFilterRoleId(undefined);
												setFilterOpen(false);
											}}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													filterRoleId === undefined ? "opacity-100" : "opacity-0"
												)}
											/>
											All Roles
										</CommandItem>
										{rolesQuery.data?.map((role) => (
											<CommandItem
												key={role.id}
												onSelect={() => {
													setFilterRoleId(role.id);
													setFilterOpen(false);
												}}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														filterRoleId === role.id ? "opacity-100" : "opacity-0"
													)}
												/>
												{role.name}
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>

					{filterRoleId && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => {
								setFilterRoleId(undefined);
							}}
						>
							<X className="mr-1 h-4 w-4" />
							Clear filter
						</Button>
					)}
				</div>

				{/* Invite User Button */}
				<Button onClick={() => { setIsInviteDialogOpen(true); }}>
					<UserPlus className="mr-2 h-4 w-4" />
					Invite User
				</Button>
			</div>

			{/* Unified Members Table */}
			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
				</div>
			) : filteredMembers.length === 0 ? (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-12">
						<Users className="h-12 w-12 text-muted-foreground mb-4" />
						<h3 className="text-lg font-medium mb-2">No Users Found</h3>
						<p className="text-muted-foreground text-center max-w-md">
							{filterRoleId
								? "No users found with this role."
								: "No users have access to this account yet. Invite someone to get started."}
						</p>
					</CardContent>
				</Card>
			) : (
				<Card>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Email</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Roles</TableHead>
								<TableHead className="w-[100px]">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredMembers.map((member) => {
								const inviteUrl = member.type === "invite"
									? `${window.location.origin}/invite?token=${member.token}`
									: null;
								const isExpired = member.type === "invite" && new Date(member.expiresAt) < new Date();

								return (
									<TableRow
										key={`${member.type}-${member.id}`}
										className="cursor-pointer hover:bg-muted/50"
										onClick={() => { handleRowClick(member); }}
									>
										<TableCell className="font-medium">{member.email}</TableCell>
										<TableCell>
											{member.type === "invite" ? (
												<Badge
													variant={isExpired ? "destructive" : "outline"}
													className="text-xs"
												>
													{isExpired ? "Expired" : "Invited"}
												</Badge>
											) : (
												<Badge variant="default" className="text-xs">
													Active
												</Badge>
											)}
										</TableCell>
										<TableCell>
											{member.type === "user" ? (
												<UserRoleBadges
													accountRole={member.accountRole}
													teams={member.teams}
												/>
											) : member.accountRole ? (
												<Badge variant="secondary" className="text-xs">
													{member.accountRole.name}
												</Badge>
											) : (
												<span className="text-muted-foreground text-sm">—</span>
											)}
										</TableCell>
										<TableCell>
											<div
												className="flex items-center gap-1"
												onClick={(e) => { e.stopPropagation(); }}
											>
												{member.type === "invite" && inviteUrl && (
													<Button
														variant="ghost"
														size="sm"
														onClick={() => {
															void navigator.clipboard.writeText(inviteUrl);
														}}
														title="Copy invite link"
													>
														<Copy className="h-4 w-4" />
													</Button>
												)}
												<Button
													variant="ghost"
													size="sm"
													onClick={() => {
														if (member.type === "invite") {
															revokeInviteMutation.mutate({ inviteId: member.id });
														}
														else {
															setRemovingUser({
																id: member.id,
																name: member.name,
																email: member.email,
															});
														}
													}}
													disabled={member.type === "invite" && revokeInviteMutation.isPending}
													title={member.type === "invite" ? "Revoke invite" : "Remove from account"}
												>
													<Trash2 className="h-4 w-4 text-red-400" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					{/* Pagination */}
					{usersQuery.data?.hasMore && (
						<div className="flex justify-center py-4 border-t">
							<Button variant="outline" disabled>
								Load More (Coming Soon)
							</Button>
						</div>
					)}
				</Card>
			)}

			{/* Invite User Dialog */}
			<InviteUserDialog
				open={isInviteDialogOpen}
				onOpenChange={setIsInviteDialogOpen}
				onSuccess={() => {
					void usersQuery.refetch();
					void invitesQuery.refetch();
				}}
			/>

			{/* Edit Invite Dialog - opens when clicking invite row */}
			{editingInvite && (
				<EditInviteDialog
					invite={editingInvite}
					onClose={() => {
						setEditingInvite(null);
					}}
					onSuccess={() => {
						void invitesQuery.refetch();
						setEditingInvite(null);
					}}
				/>
			)}

			{/* Edit User Dialog - opens when clicking user row */}
			{editingUser && (
				<EditUserDialog
					user={editingUser}
					onClose={() => {
						setEditingUser(null);
					}}
					onSuccess={() => {
						setEditingUser(null);
						void usersQuery.refetch();
					}}
				/>
			)}

			{/* Remove User Confirm Dialog */}
			{removingUser && (
				<RemoveUserConfirmDialog
					user={removingUser}
					onClose={() => {
						setRemovingUser(null);
					}}
					onSuccess={() => {
						setRemovingUser(null);
						void usersQuery.refetch();
					}}
				/>
			)}
		</div>
	);
}
